import React, { useCallback, useEffect, useState } from "react";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { useFetchNextTask } from "../api/ReportsApi";
import { useNavigate } from "react-router-dom";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useFamaFlags } from "../api/FamaFlags";
import { useReportOriginManager } from "./ReportsHooks";
import { useGetHasTasksOnlyQueue } from "../api/UsersApi";

const WAIT_BEFORE_REDIRECT = 2000;

const ReportsNextTask = () => {
  const navigate = useNavigate();
  const { showLargeToast } = useApplicationToast();
  const {
    mutate: getNextTask,
    data: url,
    error,
    isSuccess,
  } = useFetchNextTask();
  const { enableNextTaskRedirect } = useFamaFlags();
  const { setOrigin } = useReportOriginManager();
  const { hasOnlyQueue, isLoadingHasOnlyQueue } = useGetHasTasksOnlyQueue();
  const [redirectUrl, setRedirectUrl] = useState("");
  const BASE_REPORTS_URL = "/reports";

  const performRedirect = useCallback(
    (url: string, callback?: () => void) => {
      setTimeout(() => {
        navigate(url);
        callback?.();
      }, WAIT_BEFORE_REDIRECT);
    },
    [navigate]
  );

  useEffect(() => {
    if (!isLoadingHasOnlyQueue) {
      setRedirectUrl(hasOnlyQueue ? "/reports/enter_queue" : BASE_REPORTS_URL);
    }
  }, [hasOnlyQueue, isLoadingHasOnlyQueue]);

  useEffect(() => {
    if (!redirectUrl) return;
    if (enableNextTaskRedirect) {
      getNextTask();
    } else {
      performRedirect(redirectUrl);
    }
  }, [getNextTask, enableNextTaskRedirect, performRedirect, redirectUrl]);

  useEffect(() => {
    if (isSuccess && url) {
      if (url === "/reports" && hasOnlyQueue) {
        performRedirect("/reports/no_queue");
        return;
      }
      performRedirect(url ?? redirectUrl);
      setOrigin("task_assignment");
    }
  }, [url, isSuccess, performRedirect, setOrigin, redirectUrl, hasOnlyQueue]);

  useEffect(() => {
    if (error) {
      performRedirect("/reports", () => {
        showLargeToast?.({
          title: "Error!",
          message:
            "Error getting your next task please try again later or contact support.",
          icon: (
            <div className="bg-red-100 text-red-600 rounded-full p-2">
              <ExclamationTriangleIcon className="w-6" />
            </div>
          ),
        });
      });
    }
  }, [error, performRedirect, showLargeToast]);

  return (
    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-white">
      <LoadingSpinner />
      <div className="pl-2 text-gray-600">Getting your next task</div>
    </div>
  );
};

export default ReportsNextTask;
